/**
 * @spec docs/specs/features/staking-position-ui.md#phase-2-transactions
 * Transaction types and interfaces
 */

import type { ConnectedWallet } from '@privy-io/react-auth';
import type { WrongNetworkInfo } from '@/lib/network-gate';

/**
 * Transaction step states for modal state machine
 */
export type TransactionStep =
  | 'form' // User fills in the form
  | 'review' // User reviews the transaction details
  | 'approving' // Waiting for token approval (if needed)
  | 'confirming' // Waiting for user to sign in wallet
  | 'pending' // Transaction submitted, waiting for confirmation
  | 'success' // Transaction confirmed
  | 'error'; // Transaction failed

/**
 * Transaction status for tracking
 */
export type TransactionStatus = 'idle' | 'pending' | 'success' | 'error';

/** A connected Privy wallet that signs instead of the selected one (AddressPage). */
export type WalletOverride = ConnectedWallet;

/**
 * Base transaction parameters
 */
export interface BaseTransactionParams {
  /** Chain ID for the transaction */
  chainId: number;
}

/**
 * Stake transaction parameters
 */
export interface StakeParams extends BaseTransactionParams {
  /** Amount to stake in wei */
  amount: bigint;
  /** Lock duration in weeks (0 for Escalator, 4-104 for Hard Lock) */
  lockWeeks?: number;
  /** Override the wallet used for signing (instead of the global selected wallet) */
  walletOverride?: WalletOverride;
  /** Override the address (instead of the global selected address) */
  addressOverride?: string;
}

/**
 * Unstake transaction parameters
 */
export interface UnstakeParams extends BaseTransactionParams {
  /** Position token ID */
  tokenId: string;
  /** Override the wallet used for signing (instead of the global selected wallet) */
  walletOverride?: WalletOverride;
}

/**
 * Partial unstake transaction parameters
 */
export interface PartialUnstakeParams extends BaseTransactionParams {
  /** Position token ID */
  tokenId: string;
  /** Amount to unstake in wei */
  amount: bigint;
  /** Override the wallet used for signing (instead of the global selected wallet) */
  walletOverride?: WalletOverride;
}

/**
 * Withdraw transaction parameters
 */
export interface WithdrawParams extends BaseTransactionParams {
  /** Position token ID */
  tokenId: string;
  /** Override the wallet used for signing (instead of the global selected wallet) */
  walletOverride?: WalletOverride;
}

/**
 * Cancel cooldown transaction parameters
 */
export interface CancelCooldownParams extends BaseTransactionParams {
  /** Position token ID */
  tokenId: string;
  /** Override the wallet used for signing (instead of the global selected wallet) */
  walletOverride?: WalletOverride;
}

/**
 * Merge positions transaction parameters
 */
export interface MergeParams extends BaseTransactionParams {
  /** First position token ID */
  tokenId1: string;
  /** Second position token ID */
  tokenId2: string;
  /** Override the wallet used for signing (instead of the global selected wallet) */
  walletOverride?: WalletOverride;
}

/**
 * Convert to lock transaction parameters
 */
export interface ConvertToLockParams extends BaseTransactionParams {
  /** Position token ID */
  tokenId: string;
  /** Lock duration in weeks (4-104) */
  lockWeeks: number;
}

/**
 * Claim PHNM transaction parameters (gasless relay)
 */
export interface ClaimPHNMParams {
  /** Recipient address */
  address: string;
  /** Claimable amount in wei */
  amount: string;
  /** Merkle proof */
  proof: string[];
  /** Override the wallet used for signing (instead of the global selected wallet) */
  walletOverride?: WalletOverride;
}

/**
 * Claim USDT transaction parameters (direct on Polygon)
 */
export interface ClaimUSDTParams extends BaseTransactionParams {
  /** Claimable amount in smallest unit */
  amount: string;
  /** Merkle proof */
  proof: string[];
}

/**
 * Transaction result
 */
export interface TransactionResult {
  /** Whether the transaction was successful */
  success: boolean;
  /** Transaction hash (if successful) */
  transactionHash?: string;
  /** Error message (if failed) */
  error?: string;
  /** Block number (if confirmed) */
  blockNumber?: number;
  /** Additional data (like new token ID for stake) */
  data?: Record<string, unknown>;
  /** Set when the failure was a wallet on the wrong network; drives the error screen's switch action */
  wrongNetwork?: WrongNetworkInfo;
}

/**
 * Gas estimate result
 */
export interface GasEstimate {
  /** Estimated gas limit */
  gasLimit: bigint;
  /** Current gas price in wei */
  gasPrice: bigint;
  /** Estimated total cost in wei */
  totalCost: bigint;
  /** Estimated total cost in native currency */
  totalCostFormatted: string;
}

/**
 * Token approval state
 */
export interface ApprovalState {
  /** Whether approval is needed */
  needsApproval: boolean;
  /** Current allowance */
  currentAllowance: bigint;
  /** Amount needed */
  amountNeeded: bigint;
  /** Whether approval is pending */
  isPending: boolean;
}

/**
 * Transaction modal props (base)
 */
export interface TransactionModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when transaction succeeds */
  onSuccess?: () => void;
}

/**
 * Position status enum matching contract
 */
export enum PositionStatusEnum {
  Active = 0, // Escalator
  Locked = 1, // Hard Lock
  Cooldown = 2, // In cooldown
}

/**
 * Staking type selection
 */
export type StakingType = 'escalator' | 'hardlock';

/**
 * Lock duration option
 */
export interface LockDurationOption {
  weeks: number;
  label: string;
  description?: string;
}

/**
 * Predefined lock duration options
 */
export const LOCK_DURATION_OPTIONS: LockDurationOption[] = [
  { weeks: 4, label: '4 weeks', description: 'Minimum lock period' },
  { weeks: 12, label: '3 months', description: '~12 weeks' },
  { weeks: 26, label: '6 months', description: '~26 weeks' },
  { weeks: 52, label: '1 year', description: '~52 weeks' },
  { weeks: 104, label: '2 years', description: 'Maximum lock period' },
];

/**
 * Staking constants from contract
 */
export const STAKING_CONSTANTS = {
  MIN_STAKE: BigInt(1e18), // 1 PHNM
  MAX_POSITIONS: 10,
  COOLDOWN_PERIOD: 7 * 24 * 60 * 60, // 7 days in seconds
  MIN_LOCK_WEEKS: 4,
  MAX_LOCK_WEEKS: 104,
  WEEK_DURATION: 7 * 24 * 60 * 60, // 7 days in seconds
} as const;
